// IndexedDB-based offline storage for caching data
const DB_NAME = 'mypocketcoach-offline';
const DB_VERSION = 1;

export interface OfflineQueueItem {
  id: string;
  table: string;
  operation: 'insert' | 'update' | 'delete';
  data: Record<string, unknown>;
  timestamp: number;
  attempts?: number;
  lastError?: string;
  failed?: boolean;
}

export const MAX_QUEUE_ATTEMPTS = 5;

export interface CachedData<T = unknown> {
  key: string;
  data: T;
  timestamp: number;
  expiresAt: number;
}

let dbInstance: IDBDatabase | null = null;
let dbOpenPromise: Promise<IDBDatabase> | null = null;

function isConnectionClosedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: string; message?: string };
  return (
    err.name === 'InvalidStateError' ||
    (typeof err.message === 'string' && err.message.includes('database connection is closing'))
  );
}

function openDB(): Promise<IDBDatabase> {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (dbOpenPromise) return dbOpenPromise;

  dbOpenPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbOpenPromise = null;
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      dbOpenPromise = null;

      // Invalidate cached instance if the connection closes
      // (e.g. iOS suspending the page, version change, or unexpected close)
      dbInstance.onclose = () => {
        dbInstance = null;
      };
      dbInstance.onversionchange = () => {
        try {
          dbInstance?.close();
        } catch {
          // ignore
        }
        dbInstance = null;
      };

      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store for cached query data
      if (!db.objectStoreNames.contains('cache')) {
        db.createObjectStore('cache', { keyPath: 'key' });
      }

      // Store for offline mutation queue
      if (!db.objectStoreNames.contains('queue')) {
        const queueStore = db.createObjectStore('queue', { keyPath: 'id' });
        queueStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });

  return dbOpenPromise;
}

/**
 * Run an IndexedDB operation, retrying once if the cached connection has closed.
 * Swallows InvalidStateError on the retry to keep these calls non-fatal.
 */
async function withDB<T>(operation: (db: IDBDatabase) => Promise<T>, fallback: T): Promise<T> {
  try {
    const db = await openDB();
    return await operation(db);
  } catch (error) {
    if (isConnectionClosedError(error)) {
      // Connection was closed (iOS suspend, tab freeze, etc.) — reset and retry once
      dbInstance = null;
      dbOpenPromise = null;
      try {
        const db = await openDB();
        return await operation(db);
      } catch (retryError) {
        if (isConnectionClosedError(retryError)) {
          return fallback;
        }
        console.error('IndexedDB operation failed after retry:', retryError);
        return fallback;
      }
    }
    console.error('IndexedDB operation failed:', error);
    return fallback;
  }
}

// Cache operations
export async function getCachedData<T>(key: string): Promise<T | null> {
  return withDB<T | null>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('cache', 'readonly');
      const store = transaction.objectStore('cache');
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result as CachedData<T> | undefined;
        if (!result) {
          resolve(null);
          return;
        }

        // Check if expired
        if (result.expiresAt < Date.now()) {
          deleteCachedData(key).catch(() => {});
          resolve(null);
          return;
        }

        resolve(result.data);
      };

      request.onerror = () => reject(request.error);
    });
  }, null);
}

export async function setCachedData<T>(
  key: string,
  data: T,
  ttlMs: number = 1000 * 60 * 60 * 24 // 24 hours default
): Promise<void> {
  await withDB<void>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('cache', 'readwrite');
      const store = transaction.objectStore('cache');

      const cached: CachedData<T> = {
        key,
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttlMs,
      };

      const request = store.put(cached);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }, undefined);
}

export async function deleteCachedData(key: string): Promise<void> {
  await withDB<void>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('cache', 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }, undefined);
}

// Queue operations for offline mutations
export async function addToQueue(item: Omit<OfflineQueueItem, 'id' | 'timestamp'>): Promise<string> {
  const id = crypto.randomUUID();
  await withDB<void>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('queue', 'readwrite');
      const store = transaction.objectStore('queue');

      const queueItem: OfflineQueueItem = {
        ...item,
        id,
        timestamp: Date.now(),
      };

      const request = store.add(queueItem);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }, undefined);
  return id;
}

export async function getQueueItems(): Promise<OfflineQueueItem[]> {
  return withDB<OfflineQueueItem[]>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('queue', 'readonly');
      const store = transaction.objectStore('queue');
      const index = store.index('timestamp');
      const request = index.getAll();

      request.onsuccess = () => {
        const all = (request.result || []) as OfflineQueueItem[];
        // Exclude items that have permanently failed.
        resolve(all.filter((item) => !item.failed));
      };
      request.onerror = () => reject(request.error);
    });
  }, []);
}

export async function getFailedQueueItems(): Promise<OfflineQueueItem[]> {
  return withDB<OfflineQueueItem[]>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('queue', 'readonly');
      const store = transaction.objectStore('queue');
      const request = store.getAll();
      request.onsuccess = () => {
        const all = (request.result || []) as OfflineQueueItem[];
        resolve(all.filter((item) => item.failed));
      };
      request.onerror = () => reject(request.error);
    });
  }, []);
}

export async function getQueueCount(): Promise<number> {
  const items = await getQueueItems();
  return items.length;
}

export async function getFailedQueueCount(): Promise<number> {
  const items = await getFailedQueueItems();
  return items.length;
}

export async function updateQueueItem(
  id: string,
  patch: Partial<OfflineQueueItem>,
): Promise<void> {
  await withDB<void>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('queue', 'readwrite');
      const store = transaction.objectStore('queue');
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const existing = getReq.result as OfflineQueueItem | undefined;
        if (!existing) {
          resolve();
          return;
        }
        const merged = { ...existing, ...patch, id: existing.id };
        const putReq = store.put(merged);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }, undefined);
}

export async function clearFailedQueueItems(): Promise<void> {
  const failed = await getFailedQueueItems();
  for (const item of failed) {
    await removeFromQueue(item.id);
  }
}

export async function removeFromQueue(id: string): Promise<void> {
  await withDB<void>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('queue', 'readwrite');
      const store = transaction.objectStore('queue');
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }, undefined);
}

export async function clearQueue(): Promise<void> {
  await withDB<void>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('queue', 'readwrite');
      const store = transaction.objectStore('queue');
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }, undefined);
}

// Clear all cached data (useful for logout)
export async function clearAllCache(): Promise<void> {
  await withDB<void>((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('cache', 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }, undefined);
}
